import type {
  KeldaClaimingRequestDto,
  KeldaClaimResponse,
  KeldaRequestAdminGetResponseDto,
  KeldaRequestCreationDto,
  KeldaRequestGetResponseDto,
  KeldaRequestUpdateDto,
  KeldaRequestUpdateResponseDto,
} from '@mimir/common/dto/keldaRequestDtos';
import type { HttpQueryParameters, AdditionalParams } from '@mimir/common/util/apiClient';
import { apiPut, apiDelete, apiPost, apiGet } from '@mimir/common/util/http';
import { ofConfig, ofConfigStatus, ofConfigsStatus, ofKeldaAdmin, ofKeldaIngestFile, ofKeldaRequest, ofKeldaRequestClaim, ofKeldaRequests, ofKeldaS3ServerKeys, ofKeldaSupervisorConfig, ofKeldaWorkerConfig, ofWebsocketInfo } from '@mimir/common/aws/api/path';
import type {
  CreateKeldaConfigDto, KeldaConfigDto, KeldaSupervisorConfig,
  KeldaWorkerConfig, UpdateKeldaConfigDto} from '@mimir/common/dto/keldaConfigDto';
import type { KeldaStatusDto, UpdateOrCreateKeldaStatusDto } from '@mimir/common/dto/statusDto';
import type { BackendClient } from '@mimir/common/util/backendClient';
import { assumeResultObject, assumeResultArray } from '@mimir/common/client/assumeResult';
import { CreateKeldaCommand, DeleteKeldaCommand, GetKeldaCommand, ListKeldasCommand } from '@mimir/common/api/keldaCommands';
import type { S3ServerKeysPresignedURLDto } from '@mimir/common/dto/keldaS3ServerKeysTypes';
import type { KeldaAdminStatusDto } from '@mimir/common/dto/keldaAdminDto';
import { callApiCommand } from './callApiCommand';
import type { InitializedKeldaWebSocketDto } from '@mimir/common/dto/wsDtos';
import type { KeldaIngestFileDto } from '@mimir/common/dto/keldaIngestFile';

export const api_getWebsocketInfo = async (c: BackendClient, keldaId: string, keldaInstanceId: string): Promise<InitializedKeldaWebSocketDto[]> => {
  return apiGet(c.getApiClient(), ofWebsocketInfo(c.getServiceLinks(), keldaId, keldaInstanceId)).then((result) => assumeResultArray<InitializedKeldaWebSocketDto>(result));
};

export const api_getKeldaRequest = async (c: BackendClient, keldaRequestId: string): Promise<KeldaRequestGetResponseDto> => {
  const queryParams: HttpQueryParameters = {
    keldaRequestId,
  };
  return apiGet(c.getApiClient(), ofKeldaRequest(c.getServiceLinks(), '_'), {queryParams}).then((result) => assumeResultObject<KeldaRequestGetResponseDto>(result));
};


export const api_getKeldaRequests = async (c: BackendClient, supportTenantId?: string): Promise<KeldaRequestAdminGetResponseDto[]> => {
  const queryParams: HttpQueryParameters = {};
  if (supportTenantId) {
    queryParams.supportTenantId = supportTenantId;
  }
  return apiGet(c.getApiClient(), ofKeldaRequests(c.getServiceLinks()), {queryParams}).then((result) => assumeResultArray<KeldaRequestAdminGetResponseDto>(result));
};


export const api_deleteKeldaRequests = async (c: BackendClient, placeholderItemId: string, supportTenantId?: string): Promise<KeldaRequestAdminGetResponseDto[]> => {
  const queryParams: HttpQueryParameters = {
    conformItemId: placeholderItemId,
  };
  if (supportTenantId) {
    queryParams.supportTenantId = supportTenantId;
  }
  return apiDelete(c.getApiClient(), ofKeldaRequests(c.getServiceLinks()), {}, { queryParams }).then((result) => assumeResultArray<KeldaRequestAdminGetResponseDto>(result));
};


export const api_deleteKeldaRequest = async (c: BackendClient, keldaRequestId: string, supportTenantId?: string): Promise<void> => {
  const queryParams: HttpQueryParameters = {
    keldaRequestId,
  };
  if (supportTenantId) {
    queryParams.supportTenantId = supportTenantId;
  }
  await apiDelete(c.getApiClient(), ofKeldaRequest(c.getServiceLinks(), '_'), {}, { queryParams });
};

export const api_getAllKeldaStatuses = (c: BackendClient, queryParams: HttpQueryParameters = {}): Promise<KeldaAdminStatusDto[]> =>
  apiGet(c.getApiClient(), ofKeldaAdmin(c.getServiceLinks()), { queryParams }).then(result => assumeResultArray<KeldaAdminStatusDto>(result));

/************************ Kelda ************************/
export const api_createKeldaRequest = async (c: BackendClient, body: KeldaRequestCreationDto, additionalParams: AdditionalParams = {}): Promise<KeldaRequestGetResponseDto> =>
  apiPost(c.getApiClient(), ofKeldaRequests(c.getServiceLinks()), body, additionalParams).then(result => assumeResultObject<KeldaRequestGetResponseDto>(result, 'filePath'));


export const api_updateKeldaRequest = async (c: BackendClient, keldaRequestId: string, body: KeldaRequestUpdateDto): Promise<KeldaRequestUpdateResponseDto> =>
  apiPut(c.getApiClient(), ofKeldaRequest(c.getServiceLinks(), '_'), body, {
    // For updating Kelda requests we do not want to rely on the default
    // timeout of the underlying ApiClient, since a Kelda request that is not updated
    // within one minute is considered stale and can be claimed by another worker.
    timeout: 30_000,
    queryParams: {
      keldaRequestId,
    },
  }).then(result => assumeResultObject<KeldaRequestUpdateResponseDto>(result));


/**
 * @deprecated Just use CreateKeldaCommand directly.
 */
export const api_createKeldaConfig = async (c: BackendClient, body: CreateKeldaConfigDto): Promise<KeldaConfigDto> =>
  callApiCommand(c, new CreateKeldaCommand({ config: body }), undefined);


export const api_updateKeldaConfig = async (c: BackendClient, body: UpdateKeldaConfigDto): Promise<KeldaConfigDto> =>
  apiPut(c.getApiClient(), ofConfig(c.getServiceLinks(), 'kelda', body.id), body).then(result => assumeResultObject<KeldaConfigDto>(result, 'indexedFolders'));


/**
 * @deprecated Just use ListKeldasCommand directly.
 */
export const api_getKeldaConfigs = async (c: BackendClient, additionalParams: AdditionalParams = {}): Promise<KeldaConfigDto[]> =>
  callApiCommand(c, new ListKeldasCommand({}), additionalParams);


/**
 * @deprecated Just use GetKeldaCommand directly.
 */
export const api_getKeldaConfig = async (c: BackendClient, configId: string): Promise<KeldaConfigDto> =>
  callApiCommand(c, new GetKeldaCommand({ configId }), undefined);


export const api_getKeldaWorkerConfig = async (c: BackendClient, id: string): Promise<KeldaWorkerConfig> =>
  apiGet(c.getApiClient(), ofKeldaWorkerConfig(c.getServiceLinks(), id)).then(result => assumeResultObject<KeldaWorkerConfig>(result, 'stabilityThreshold'));


export const api_getKeldaSupervisorConfig = async (
  c: BackendClient,
  id: string,
  keldaVersion: string,
  hostname: string,
  keldaInstanceId: string,
  cpuArch: 'aarch64' | 'x86_64',
): Promise<KeldaSupervisorConfig> =>
  apiGet(c.getApiClient(), ofKeldaSupervisorConfig(c.getServiceLinks(), id), {
    queryParams: {
      keldaVersion,
      hostname,
      keldaInstanceId,
      cpuArch,
    }
  }).then(result => assumeResultObject<KeldaSupervisorConfig>(result, 'workerConfig'));

/**
 * @deprecated Just use DeleteKeldaCommand directly.
 */
export const api_deleteKeldaConfig = async (c: BackendClient, configId: string): Promise<void> =>
  callApiCommand(c, new DeleteKeldaCommand({ configId }), undefined);


export const api_getKeldaStatuses = async (c: BackendClient): Promise<KeldaStatusDto[]> =>
  apiGet(c.getApiClient(), ofConfigsStatus(c.getServiceLinks(), 'kelda')).then(result => assumeResultArray<KeldaStatusDto>(result));


export const api_getKeldaStatusesForConfiguration = async (c: BackendClient, configId: string): Promise<KeldaStatusDto[]> =>
  apiGet(c.getApiClient(), ofConfigStatus(c.getServiceLinks(), 'kelda', configId)).then(result => assumeResultArray<KeldaStatusDto>(result));


export const api_updateOrCreateKeldaStatus = async (c: BackendClient, configId: string, updateOrCreateDto: UpdateOrCreateKeldaStatusDto): Promise<KeldaStatusDto> =>
  apiPut(c.getApiClient(), ofConfigStatus(c.getServiceLinks(), 'kelda', configId), updateOrCreateDto).then(result => assumeResultObject<KeldaStatusDto>(result, 'storageFolders'));

export const api_claimKeldaRequest = async (
  c: BackendClient,
  body: KeldaClaimingRequestDto
): Promise<KeldaClaimResponse> =>
  apiPost(c.getApiClient(), ofKeldaRequestClaim(c.getServiceLinks()), { ...body, supportsRetryAfter: true })
    .then(result => assumeResultObject<KeldaClaimResponse>(result));

export const api_getKeldaS3ServerKeysPresignedURL = async (c: BackendClient, keldaId: string, additionalParams: AdditionalParams = {}): Promise<S3ServerKeysPresignedURLDto> => {
  return apiGet(c.getApiClient(), ofKeldaS3ServerKeys(c.getServiceLinks(), keldaId), additionalParams).then(result => assumeResultObject(result));
};

export async function api_keldaIngestFile(
  c: BackendClient,
  keldaId: string,
  dto: KeldaIngestFileDto,
) {
  await apiPut(c.getApiClient(), ofKeldaIngestFile(c.getServiceLinks(), keldaId), dto);
}
